import React from 'react';
import { get } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, Tabs, Tab, Divider, BoxProps, Container } from '@mui/material';

import { links } from '../../paths';
import { useSelector } from '../../store/utils';
import { isSolidsunIframe } from '../../utils/url-layout-modes';
import { useEmsInstalled } from '../../utils/ems';

type Props = {
  //
} & BoxProps;

export const NavSecondary = ({ ...props }: Props) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [
    hpEnabled,
    hwInstall,
    spInstall,
    plInstall,
    pvInstall,
    wbInstall,
    emInstall,
    windInstall,
    cloudPlugsInstall,
    thermInstall,
    relayInstall,
    customTabs,
    emMetersConfig,
  ] = useSelector(
    ({ variables }) =>
      [
        get(variables, 'HP_ENABLED'),
        get(variables, 'HW_INSTALL'),
        get(variables, 'SP_INSTALL'),
        get(variables, 'PL_INSTALL'),
        get(variables, 'PV_INSTALL'),
        get(variables, 'WB_INSTALL'),
        get(variables, 'EM_INSTALL'),
        get(variables, 'WIND_INSTALL'),
        get(variables, 'CLOUD_PLUGS_INSTALL'),
        get(variables, 'THERMOSTAT_INSTALL'),
        get(variables, 'RELAY_INSTALL') &&
          get(variables, 'RELAY_USER_ADVANCED_CONTROL'),
        get(variables, 'APP_UI_CUSTOM_TABS') || [],
        get(variables, 'EM_METERS_CONFIG') || {},
      ] as const
  );

  const emsInstall = useEmsInstalled();

  const items = [
    {
      link: links.home,
      label: <FormattedMessage id="nav.sec.home" defaultMessage="Obecné" />,
      hidden: isSolidsunIframe(),
    },
    {
      link: links.photovoltaics,
      label: (
        <FormattedMessage
          id="nav.sec.photovoltaics"
          defaultMessage="Fotovoltaika"
        />
      ),
      hidden: !pvInstall,
    },
    {
      link: links.ems,
      label: <FormattedMessage id="nav.sec.ems" defaultMessage="EMS" />,
      hidden: !emsInstall,
    },
    {
      link: links.boiler,
      label: <FormattedMessage id="nav.sec.boiler" defaultMessage="Bojler" />,
      hidden: !hwInstall,
    },
    {
      link: links.grid,
      label: <FormattedMessage id="nav.sec.grid" defaultMessage="Síť" />,
      hidden: !emInstall,
    },
    {
      link: links.heatPump,
      label: (
        <FormattedMessage
          id="nav.sec.heatpump"
          defaultMessage="Tepelné čerpadlo"
        />
      ),
      hidden: !hpEnabled,
    },
    {
      link: links.wallbox,
      label: <FormattedMessage id="nav.sec.wallbox" defaultMessage="Wallbox" />,
      hidden: !wbInstall,
    },
    {
      link: links.plug,
      label: <FormattedMessage id="nav.sec.plug" defaultMessage="Zásuvka" />,
      hidden: !plInstall,
    },
    {
      link: links.thermostat,
      label: (
        <FormattedMessage id="nav.sec.thermostat" defaultMessage="Termostat" />
      ),
      hidden: !thermInstall,
    },
    {
      link: links.relay,
      label: <FormattedMessage id="nav.sec.relay" defaultMessage="Relé" />,
      hidden: !relayInstall,
    },
    {
      link: links.energyMeters,
      label: (
        <FormattedMessage
          id="nav.sec.energyMeters"
          defaultMessage="Elektroměry"
        />
      ),
      hidden: Object.keys(emMetersConfig).length === 0,
    },
    {
      link: links.spot,
      label: <FormattedMessage id="nav.sec.spot" defaultMessage="Spot" />,
      hidden: !spInstall,
    },
    {
      link: links.wind,
      label: (
        <FormattedMessage
          id="nav.sec.wind"
          defaultMessage="Větrná elektrárna"
        />
      ),
      hidden: !windInstall,
    },
    {
      link: links.cloudPlugs,
      label: (
        <FormattedMessage id="nav.sec.cloud_plugs" defaultMessage="Zásuvky" />
      ),
      hidden: !cloudPlugsInstall,
    },
    ...customTabs.map(({ label, url }) => ({
      link: links.customPage.replace(':tabUrl', url),
      label: label,
      hidden: false,
    })),
  ];

  const visibleItems = items.filter(({ hidden }) => !hidden);
  const tabsValue = items.find(({ link }) => link === pathname)
    ? pathname
    : false;

  return (
    <Box component="nav" {...props}>
      <Container>
        <Tabs
          value={tabsValue}
          onChange={(_e, link) => navigate(link)}
          variant="scrollable"
          sx={{
            mx: -1,
            '&, & .MuiButtonBase-root': {
              minHeight: 32,
            },
            '& .MuiButtonBase-root': {
              minWidth: 0,
              px: 0,
              mx: 1,
              mr: { xs: 1, md: 6 },
              textTransform: 'none',
              '&:not(.Mui-selected)': {
                fontWeight: 400,
              },
            },
          }}
        >
          {visibleItems.map(({ label, link }) => (
            <Tab label={label} value={link} key={link} sx={{ height: 20 }} />
          ))}
        </Tabs>
      </Container>

      <Divider />
    </Box>
  );
};
